import { cloneDeep } from "lodash";
import {
  TWebsiteBuilderNode,
  isWebsiteBuilderParentNode,
} from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderNode";
import {
  IWebsiteBuilderRoot,
  WEBSITE_BUILDER_ROOT_CLASS_NAME,
} from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderRoot";
import { IWebsiteBuilderSelectedNode } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderSelectedNode";
import {
  applyTreeChange,
  applyUndoTreeChange,
  TWebsiteBuilderAction,
  WebsiteBuilderActionType,
} from "redux/actions/websiteBuilder/websiteBuilderAction";
import { IWebsiteBuilderGlobalStyles, IWebsiteBuilderTheme } from "../models/websiteBuilder";
import { WEBSITE_BUILDER_SECTION_CLASS_NAME } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderSection";
import {
  isWebsiteBuilderStretchElementData,
  WEBSITE_BUILDER_ELEMENT_CLASS_NAME,
} from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderElement";
import { generateButtonDesignStyles } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderButton";
import { generateDividerDesignStyles } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderDivider";
import { generateParagraphDesignStyles } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderParagraph";
import { generateInputDesignStyles } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderInput";
import { WEBSITE_BUILDER_DESIGN_PAGE_WIDTH } from "pages/secure/facility/websiteBuilder/editor/designSpace/WebsiteBuilderDesignSpace";
import { generateSelectDesignStyles } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderSelect";
import { generateHeadingDesignStyles } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderHeading";

type TWebsiteBuilderMode = "design" | "preview";
export type TWebsiteBuilderTreeNodeMap = Map<string, TWebsiteBuilderNode>;
export type TWebsiteBuilderCSSVariables = Record<`--wb-theme-${string}` | `--wb-global-${string}`, string>;

interface IWebsiteBuilderStoreState {
  mode: TWebsiteBuilderMode;
  preview: HTMLElement;
  tree: IWebsiteBuilderRoot;
  treeNodeMap: TWebsiteBuilderTreeNodeMap;
  actions: TWebsiteBuilderAction[];
  actionIndex: number;
  selectedNode: IWebsiteBuilderSelectedNode;
  theme: IWebsiteBuilderTheme;
  availableThemes: IWebsiteBuilderTheme[];
  globalStyles: IWebsiteBuilderGlobalStyles;
  cssVariables: TWebsiteBuilderCSSVariables;
}

function generateTreeNodeMap(tree: IWebsiteBuilderRoot): TWebsiteBuilderTreeNodeMap {
  const map: TWebsiteBuilderTreeNodeMap = new Map();

  if (tree == null) {
    return map;
  }

  function populateMap(node: TWebsiteBuilderNode): void {
    map.set(node.id, node);

    if (isWebsiteBuilderParentNode(node)) {
      if (node.type === "root") {
        populateMap(node.header);
      }

      node.children.forEach(child => {
        populateMap(child);
      });

      if (node.type === "root") {
        populateMap(node.footer);
      }
    }
  }

  populateMap(tree);

  return map;
}

function generatePageBodyContent(tree: IWebsiteBuilderRoot, cssVariables: TWebsiteBuilderCSSVariables): HTMLElement {
  function traverse(node: TWebsiteBuilderNode): HTMLElement {
    if (node.type === "root") {
      const root = document.createElement("div");

      root.className = `${WEBSITE_BUILDER_ROOT_CLASS_NAME} website-root`;

      Object.entries(cssVariables).forEach(([key, value]) => {
        root.style.setProperty(key, value);
      });

      root.appendChild(traverse(node.header));

      const main = document.createElement("main");

      main.className = "website-main";

      node.children.forEach(child => {
        main.appendChild(traverse(child));
      });

      root.appendChild(main);

      root.appendChild(traverse(node.footer));

      return root;
    } else if (node.type === "section") {
      let section: HTMLElement = null;

      if (node.section.type === "header") {
        section = document.createElement("header");
      } else if (node.section.type === "content") {
        section = document.createElement("section");
      } else if (node.section.type === "footer") {
        section = document.createElement("footer");
      } else {
        return null;
      }

      section.className = WEBSITE_BUILDER_SECTION_CLASS_NAME;
      section.style.height = `${node.section.height}px`;

      const sectionBounded = document.createElement("div");

      sectionBounded.className = "website-section-bounded";
      sectionBounded.style.width = `${WEBSITE_BUILDER_DESIGN_PAGE_WIDTH}px`;

      node.children.forEach(child => {
        if (
          child.type === "element" &&
          isWebsiteBuilderStretchElementData(child.element) &&
          child.element.stretch.enabled
        ) {
          section.appendChild(traverse(child));
        } else {
          sectionBounded.appendChild(traverse(child));
        }
      });

      section.appendChild(sectionBounded);

      return section;
    } else if (node.type === "container") {
      return null;
    } else if (node.type === "element") {
      const element = document.createElement("div");

      element.className = WEBSITE_BUILDER_ELEMENT_CLASS_NAME;

      const position = node.element.position;
      const dimensions = node.element.dimensions;

      element.style.left = `${position.x}px`;
      element.style.top = `${position.y}px`;
      element.style.width = `${dimensions.width}px`;
      element.style.height = `${dimensions.height}px`;
      element.style.zIndex = String(node.element.zIndex);

      if (isWebsiteBuilderStretchElementData(node.element)) {
        const stretch = node.element.stretch;

        if (stretch.enabled) {
          element.style.left = "0px";
          element.style.width = "100%";
          element.style.padding = `0px ${stretch.margin}px`;
        }
      }

      let content: HTMLElement = null;

      if (node.element.type === "button") {
        content = document.createElement("button");

        const settings = node.element.settings;
        const styles = generateButtonDesignStyles(settings);

        content.className = styles.className;
        content.style.color = styles.style.color;
        content.style.backgroundColor = styles.style.backgroundColor;
        content.style.borderColor = styles.style.borderColor;

        content.textContent = settings.label;
        content.dataset.link = settings.link;
      } else if (node.element.type === "divider") {
        content = document.createElement("hr");

        const styles = generateDividerDesignStyles(node.element.settings);

        content.className = styles.className;
        content.style.borderTopWidth = `${styles.style.borderTopWidth}px`;
        content.style.borderTopStyle = styles.style.borderTopStyle;
      } else if (node.element.type === "heading") {
        const settings = node.element.settings;

        content = document.createElement(`h${settings.level}`);

        const styles = generateHeadingDesignStyles();

        content.className = styles.className;
        content.textContent = settings.text;
      } else if (node.element.type === "input") {
        const label = document.createElement("label");
        const labelContent = document.createElement("div");
        const input = document.createElement("input");

        const settings = node.element.settings;
        const styles = generateInputDesignStyles(settings);

        label.className = styles.labelClassName;
        labelContent.className = styles.labelContentClassName;
        input.className = styles.className;

        labelContent.textContent = settings.label;

        input.placeholder = settings.placeholder;
        input.dataset.type = settings.type;
        input.dataset.required = String(settings.required);

        label.appendChild(labelContent);
        label.appendChild(input);

        content = label;
      } else if (node.element.type === "paragraph") {
        content = document.createElement("paragraph");

        const settings = node.element.settings;
        const styles = generateParagraphDesignStyles(settings);

        content.className = styles.className;

        content.textContent = settings.text;
      } else if (node.element.type === "select") {
        const label = document.createElement("label");
        const labelContent = document.createElement("div");
        const select = document.createElement("select");

        const settings = node.element.settings;
        const styles = generateSelectDesignStyles();

        label.className = styles.labelClassName;
        labelContent.className = styles.labelContentClassName;
        select.className = styles.className;

        labelContent.textContent = settings.label;

        select.name = node.id;

        const addSettingsOption = (settingsOption: string) => {
          const option = document.createElement("option");

          option.value = settingsOption;
          option.textContent = settingsOption;

          select.appendChild(option);
        };

        if (!settings.required && settings.options.length > 0) {
          addSettingsOption("");
        }

        settings.options.forEach(addSettingsOption);

        label.appendChild(labelContent);
        label.appendChild(select);

        content = label;
      } else {
        return null;
      }

      element.appendChild(content);

      return element;
    } else {
      return null;
    }
  }

  const preview = traverse(tree);

  return preview;
}

const defaultTheme: IWebsiteBuilderTheme = {
  id: 0,
  title: "Default",
  primary: "#000000",
  secondary: "#C8C8C8",
  tertiary: "#E1E1E1",
  quaternary: "#FFFFFF",
};

function generateCssVariables(
  theme: IWebsiteBuilderTheme,
  globalStyles: IWebsiteBuilderGlobalStyles,
): TWebsiteBuilderCSSVariables {
  const themeToApply = theme ?? defaultTheme;

  const vars: TWebsiteBuilderCSSVariables = {
    "--wb-theme-primary": themeToApply.primary,
    "--wb-theme-secondary": themeToApply.secondary,
    "--wb-theme-tertiary": themeToApply.tertiary,
    "--wb-theme-quaternary": themeToApply.quaternary,
  };

  if (!globalStyles) {
    return vars;
  }

  const { heading, paragraph, button } = globalStyles;

  if (heading?.color) {
    vars["--wb-global-heading-color"] = heading.color;
  }
  if (heading?.font) {
    vars["--wb-global-heading-font"] = heading.font;
  }

  if (paragraph?.color) {
    vars["--wb-global-paragraph-color"] = paragraph.color;
  }
  if (paragraph?.font) {
    vars["--wb-global-paragraph-font"] = paragraph.font;
  }
  if (paragraph?.fontSize) {
    vars["--wb-global-paragraph-font-size"] = `${paragraph.fontSize}px`;
  }

  if (button?.color) {
    vars["--wb-global-button-color"] = button.color;
  }
  if (button?.backgroundColor) {
    vars["--wb-global-button-background-color"] = button.backgroundColor;
  }
  if (button?.borderColor) {
    vars["--wb-global-button-border-color"] = button.borderColor;
  }

  return vars;
}

function containsSelectedNode(node: TWebsiteBuilderNode, selectedNodeId: string): boolean {
  if (selectedNodeId == null) {
    return false;
  }

  if (node.id === selectedNodeId || node.type === "root") {
    return true;
  }

  if (isWebsiteBuilderParentNode(node)) {
    return node.children.some(child => containsSelectedNode(child, selectedNodeId));
  } else {
    return false;
  }
}

function handleTreeModifyingActions(
  state: IWebsiteBuilderStoreState,
  action: TWebsiteBuilderAction,
): IWebsiteBuilderStoreState {
  const canUndo = state.actions.length > 0 && state.actionIndex >= 0;
  const canRedo = state.actions.length > 0 && state.actionIndex < state.actions.length - 1;

  if (action.type === WebsiteBuilderActionType.UNDO) {
    if (canUndo) {
      const actionToUndo = state.actions[state.actionIndex];

      return {
        ...state,
        tree: applyUndoTreeChange(state.tree, actionToUndo),
        actionIndex: state.actionIndex - 1,
        selectedNode:
          actionToUndo.type === WebsiteBuilderActionType.ADD_NODE &&
          containsSelectedNode(actionToUndo.payload.snapshot.node, state.selectedNode?.id)
            ? null
            : state.selectedNode,
      };
    }

    return state;
  }

  if (action.type === WebsiteBuilderActionType.REDO) {
    if (canRedo) {
      const nextActionIndex = state.actionIndex + 1;
      const actionToRedo = state.actions[nextActionIndex];

      return {
        ...state,
        tree: applyTreeChange(state.tree, actionToRedo),
        actionIndex: nextActionIndex,
        selectedNode:
          actionToRedo.type === WebsiteBuilderActionType.DELETE_NODE &&
          containsSelectedNode(actionToRedo.payload.snapshot.node, state.selectedNode?.id)
            ? null
            : state.selectedNode,
      };
    }

    return state;
  }

  let newActions = state.actions;

  if (canRedo) {
    newActions = state.actions.slice(0, state.actionIndex + 1);
  }

  let updatedSelectedNode: IWebsiteBuilderSelectedNode = state.selectedNode;

  if (
    action.type === WebsiteBuilderActionType.DELETE_NODE &&
    containsSelectedNode(action.payload.snapshot.node, state.selectedNode?.id)
  ) {
    updatedSelectedNode = null;
  } else if (
    action.type === WebsiteBuilderActionType.MOVE_ELEMENT ||
    action.type === WebsiteBuilderActionType.RESIZE_ELEMENT ||
    action.type === WebsiteBuilderActionType.RESIZE_SECTION
  ) {
    updatedSelectedNode = {
      ...state.selectedNode,
      editingMode: "none",
    };
  }

  const updatedState: IWebsiteBuilderStoreState = {
    ...state,
    tree: applyTreeChange(state.tree, action),
    actions: [...newActions, action],
    actionIndex: state.actionIndex + 1,
    selectedNode: updatedSelectedNode,
  };

  return updatedState;
}

const initialTree: IWebsiteBuilderRoot = {
  id: "root",
  type: "root",
  header: {
    id: "header",
    type: "section",
    section: {
      type: "header",
      height: 150,
    },
    children: [],
  },
  children: [
    {
      id: crypto.randomUUID(),
      type: "section",
      section: {
        type: "content",
        height: 150,
      },
      children: [],
    },
    {
      id: crypto.randomUUID(),
      type: "section",
      section: {
        type: "content",
        height: 150,
      },
      children: [],
    },
    {
      id: crypto.randomUUID(),
      type: "section",
      section: {
        type: "content",
        height: 150,
      },
      children: [],
    },
  ],
  footer: {
    id: "footer",
    type: "section",
    section: {
      type: "footer",
      height: 150,
    },
    children: [],
  },
};

const initialState: IWebsiteBuilderStoreState = {
  mode: "design",
  preview: null,
  tree: initialTree,
  treeNodeMap: generateTreeNodeMap(initialTree),
  actions: [],
  actionIndex: -1,
  selectedNode: null,
  theme: null,
  availableThemes: null,
  globalStyles: null,
  cssVariables: null,
};

const allowedActionTypes = new Set(Object.values(WebsiteBuilderActionType));

export function WebsiteBuilderReducer(state = initialState, action: TWebsiteBuilderAction): IWebsiteBuilderStoreState {
  if (!allowedActionTypes.has(action?.type) || state.tree == null) {
    return state;
  }

  if (action.type === WebsiteBuilderActionType.EXIT_EDITOR) {
    return cloneDeep(initialState);
  }

  if (action.type === WebsiteBuilderActionType.SET_DESIGN_MODE) {
    return {
      ...state,
      mode: "design",
      preview: null,
    };
  }

  if (action.type === WebsiteBuilderActionType.SET_PREVIEW_MODE) {
    return {
      ...state,
      mode: "preview",
      preview: generatePageBodyContent(state.tree, state.cssVariables),
    };
  }

  if (action.type === WebsiteBuilderActionType.LOAD_THEMES) {
    return {
      ...state,
      theme: action.payload.theme,
      availableThemes: action.payload.availableThemes,
      cssVariables: generateCssVariables(action.payload.theme, state.globalStyles),
    };
  }

  if (action.type === WebsiteBuilderActionType.SELECT_THEME) {
    const foundTheme = state.availableThemes.find(theme => theme.id === action.payload.id);

    if (!foundTheme) {
      return state;
    }

    return {
      ...state,
      theme: foundTheme,
      availableThemes: [...state.availableThemes.filter(theme => theme.id !== action.payload.id), state.theme],
      cssVariables: generateCssVariables(foundTheme, state.globalStyles),
    };
  }

  if (action.type === WebsiteBuilderActionType.LOAD_GLOBAL_STYLES) {
    return {
      ...state,
      globalStyles: action.payload.globalStyles,
      cssVariables: generateCssVariables(state.theme, action.payload.globalStyles),
    };
  }

  if (action.type === WebsiteBuilderActionType.UPDATE_GLOBAL_STYLES) {
    return {
      ...state,
      globalStyles: action.payload.globalStyles,
      cssVariables: generateCssVariables(state.theme, action.payload.globalStyles),
    };
  }

  if (action.type === WebsiteBuilderActionType.SELECT_NODE) {
    return {
      ...state,
      selectedNode: action.payload.selectedNode,
    };
  }

  if (action.type === WebsiteBuilderActionType.UPDATE_SELECTED_NODE) {
    if (state.selectedNode) {
      return {
        ...state,
        selectedNode: {
          ...state.selectedNode,
          ...action.payload.selectedNodeUpdates,
        },
      };
    }

    return state;
  }

  const result: IWebsiteBuilderStoreState = handleTreeModifyingActions(state, action);

  return {
    ...result,
    treeNodeMap: generateTreeNodeMap(result.tree),
  };
}
